import { Injectable } from '@angular/core';

import { BuilderApiEndpointsConfig, BuilderLayoutConfig } from './self-service-config.types';
import { setSelfServiceApiOverrides } from './self-service-api.utils';

/**
 * Applies the builder JSON config's `apiEndpoints` block to the module-level URL
 * overrides used by self-service-api.utils. This is the dependency-inversion seam
 * that lets the feature get its backend URLs from JSON instead of importing the
 * app `environment` directly (a prerequisite for moving the feature into the
 * shared library).
 *
 * Behaviour:
 *  - Each builder calls `applyFromLayout(layout)` after its config resolves.
 *  - The FIRST non-empty `apiEndpoints` block wins (the URLs are global to the
 *    feature — every builder talks to the same backend — so we don't want a later
 *    builder's empty/absent block to clobber an earlier one).
 *  - When no builder supplies `apiEndpoints`, the overrides stay empty and the
 *    utils fall back to `environment` exactly as before (zero behaviour change).
 */
@Injectable({ providedIn: 'root' })
export class SelfServiceApiConfigService {
  private applied = false;

  /** Apply the layout's apiEndpoints once. Safe to call from every builder. */
  applyFromLayout(layout: BuilderLayoutConfig | undefined | null): void {
    if (this.applied) return;
    const endpoints = layout?.apiEndpoints;
    if (!endpoints || !this.hasAnyUrl(endpoints)) return;
    setSelfServiceApiOverrides({
      baseUrl: endpoints.baseUrl,
      uploadUrl: endpoints.uploadUrl,
      chatUrl: endpoints.chatUrl,
    });
    this.applied = true;
  }

  private hasAnyUrl(endpoints: BuilderApiEndpointsConfig): boolean {
    return !!(
      endpoints.baseUrl?.trim() ||
      endpoints.uploadUrl?.trim() ||
      endpoints.chatUrl?.trim()
    );
  }
}
